/**
 * SimUploadProgressBanner
 *
 * A fixed bottom banner that shows Override Sim File uploads that are running
 * in the background (i.e. the user saved the run and closed the modal while
 * the browser was still uploading to Azure Blob temp storage).
 *
 * Subscribes to simUploadQueueStore directly – no Recoil, no prop drilling.
 */

import { FC, useEffect, useState, memo } from 'react'
import { classNames } from 'primereact/utils'
import { LoadingIcon } from './Icons'
import simUploadQueueStore, {
  PendingSimUpload,
  SimUploadStatus
} from '../services/simUploadQueueStore'

const statusLabel: Record<SimUploadStatus, string> = {
  uploading: 'Uploading to Azure…',
  finalizing: 'Finalizing…',
  decompressing: 'Decompressing files…',
  done: 'Upload complete',
  failed: 'Upload failed',
  cancelled: 'Upload cancelled'
}

const statusColor: Record<SimUploadStatus, string> = {
  uploading: 'border-gm-blue',
  finalizing: 'border-gm-aqua-light',
  decompressing: 'border-gm-aqua-light',
  done: 'border-green-500',
  failed: 'border-red-500',
  cancelled: 'border-gray-400'
}

const statusDot: Record<SimUploadStatus, string> = {
  uploading: 'bg-gm-blue',
  finalizing: 'bg-gm-aqua-light',
  decompressing: 'bg-gm-aqua-light',
  done: 'bg-green-500',
  failed: 'bg-red-500',
  cancelled: 'bg-gray-400'
}

type UploadRowProps = {
  upload: PendingSimUpload
}

// Memoized to prevent unnecessary re-renders when other uploads update
const UploadRow = memo<UploadRowProps>(({ upload }) => {
  const isActive =
    upload.status === 'uploading' ||
    upload.status === 'finalizing' ||
    upload.status === 'decompressing'

  return (
    <div
      className={classNames(
        'flex items-center gap-3 px-4 py-2 border-l-4',
        statusColor[upload.status]
      )}>
      {/* Status indicator */}
      {isActive ? (
        <LoadingIcon className="w-4 h-4 fill-gm-blue animate-spin flex-shrink-0" />
      ) : (
        <span
          className={classNames('w-3 h-3 rounded-full flex-shrink-0', statusDot[upload.status])}
        />
      )}

      {/* Text */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs font-medium text-gm-black dark:text-white truncate">
          {upload.fileName}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {upload.runName} — {statusLabel[upload.status]}
          {(upload.status === 'uploading' || upload.status === 'decompressing') &&
          upload.progress !== undefined
            ? ` ${upload.progress}%`
            : ''}
          {upload.status === 'failed' && upload.error && `: ${upload.error}`}
        </span>
        {/* Progress bar – shown while actively uploading or decompressing */}
        {(upload.status === 'uploading' || upload.status === 'decompressing') && (
          <div className="mt-1 w-full bg-gray-200 dark:bg-neutral-70 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-gm-blue h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${upload.progress ?? 0}%` }}
            />
          </div>
        )}
      </div>

      {/* Dismiss button for terminal states */}
      {!isActive && (
        <button
          className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm flex-shrink-0"
          onClick={() => simUploadQueueStore.dismiss(upload.runId)}
          title="Dismiss">
          ✕
        </button>
      )}
    </div>
  )
})

const SimUploadProgressBanner: FC = () => {
  const [uploads, setUploads] = useState<PendingSimUpload[]>(simUploadQueueStore.getAll())
  const [minimized, setMinimized] = useState(false)

  useEffect(() => {
    const unsub = simUploadQueueStore.subscribe(setUploads)
    return unsub
  }, [])

  if (uploads.length === 0) return null

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 flex flex-col divide-y divide-gray-200 dark:divide-neutral-70 bg-white dark:bg-dark-blue shadow-lg border-t border-gray-200 dark:border-neutral-70"
      role="status"
      aria-live="polite">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-1 bg-light-blue dark:bg-neutral-80">
        <span className="text-xs font-semibold text-gm-blue dark:text-gm-aqua-light uppercase tracking-wide">
          Background File Uploads ({uploads.length})
        </span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Do not close your browser until all uploads complete
          </span>
          <button
            type="button"
            className="border-none bg-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer px-1"
            onClick={() => setMinimized(prev => !prev)}
            title={minimized ? 'Expand uploads' : 'Minimize uploads'}
            aria-label={minimized ? 'Expand uploads' : 'Minimize uploads'}>
            <i
              className={classNames('pi text-xs', minimized ? 'pi-chevron-up' : 'pi-chevron-down')}
            />
          </button>
        </div>
      </div>

      {/* One row per upload – hidden when minimized */}
      {!minimized && uploads.map(u => <UploadRow key={u.runId} upload={u} />)}
    </div>
  )
}

export default SimUploadProgressBanner
